import { hasAny, isOneOf } from "@arti/utilities";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import type { TimingFilters, TimingKind } from "../../types/timings";

import {
  normalizeListFilter,
  normalizeNumberFilter,
  normalizeTextFilter,
} from "../../utils/dateFilters";
import { type FilterField, FiltersToolbar } from "../common/FiltersToolbar";

export interface TimingsFiltersPanelProps {
  filters: Required<Pick<TimingFilters, "fromTimestampMs" | "toTimestampMs">> &
    Omit<TimingFilters, "fromTimestampMs" | "toTimestampMs">;
  isLoading: boolean;
  onSetFilters: (patch: TimingFilters) => void;
  onSetDateRange: (fromTimestampMs: number, toTimestampMs: number) => void;
  onReset: () => void;
  onRefresh: () => void;
}

interface KindsDraft {
  canonicalValue: string;
  text: string;
}

export const TimingsFiltersPanel: FC<TimingsFiltersPanelProps> = ({
  filters,
  isLoading,
  onSetFilters,
  onSetDateRange,
  onReset,
  onRefresh,
}) => {
  const canonicalKinds = filters.kinds?.join(", ") ?? "";
  const [kindsDraft, setKindsDraft] = useState<KindsDraft | null>(null);

  useEffect(() => {
    setKindsDraft((current) =>
      current && current.canonicalValue !== canonicalKinds ? null : current,
    );
  }, [canonicalKinds]);

  const handleKindsChange = useCallback(
    (text: string) => {
      const validKinds = normalizeListFilter(text)?.filter((kind) =>
        isOneOf<TimingKind>(kind, [
          "phase",
          "model_stage",
          "tool",
          "step",
          "run",
        ]),
      );
      const kinds = hasAny(validKinds) ? validKinds : undefined;
      setKindsDraft({ canonicalValue: kinds?.join(", ") ?? "", text });
      onSetFilters({ kinds });
    },
    [onSetFilters],
  );

  const handleReset = useCallback(() => {
    setKindsDraft(null);
    onReset();
  }, [onReset]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.taskId) {
      count++;
    }
    if (filters.ticketId) {
      count++;
    }
    if (filters.streamId) {
      count++;
    }
    if (filters.runId) {
      count++;
    }
    if (filters.stepNumber !== undefined) {
      count++;
    }
    if (filters.minDurationMs !== undefined) {
      count++;
    }
    if (filters.maxDurationMs !== undefined) {
      count++;
    }
    if (filters.kinds && filters.kinds.length > 0) {
      count++;
    }
    if (filters.names && filters.names.length > 0) {
      count++;
    }
    return count;
  }, [filters]);

  const fields: FilterField[] = useMemo(
    () => [
      {
        helperText: "Comma-separated list (e.g. phase, tool).",
        key: "kinds",
        label: "Kinds",
        onChange: handleKindsChange,
        placeholder: "phase, tool",
        value: kindsDraft?.text ?? canonicalKinds,
      },
      {
        key: "names",
        label: "Name",
        onChange: (value) =>
          onSetFilters({ names: normalizeListFilter(value) }),
        placeholder: "comma-separated",
        value: filters.names?.join(", ") ?? "",
      },
      {
        key: "taskId",
        label: "Task",
        onChange: (value) =>
          onSetFilters({ taskId: normalizeTextFilter(value) }),
        placeholder: "task_...",
        value: filters.taskId ?? "",
      },
      {
        key: "ticketId",
        label: "Ticket",
        onChange: (value) =>
          onSetFilters({ ticketId: normalizeTextFilter(value) }),
        placeholder: "KAN-123",
        value: filters.ticketId ?? "",
      },
      {
        key: "streamId",
        label: "Stream",
        onChange: (value) =>
          onSetFilters({ streamId: normalizeTextFilter(value) }),
        placeholder: "stream_...",
        value: filters.streamId ?? "",
      },
      {
        key: "runId",
        label: "Run",
        onChange: (value) =>
          onSetFilters({ runId: normalizeTextFilter(value) }),
        placeholder: "run_...",
        value: filters.runId ?? "",
      },
      {
        key: "stepNumber",
        label: "Step",
        onChange: (value) =>
          onSetFilters({ stepNumber: normalizeNumberFilter(value) }),
        placeholder: "0",
        type: "number",
        value: filters.stepNumber?.toString() ?? "",
      },
      {
        key: "minDurationMs",
        label: "Min duration (ms)",
        onChange: (value) =>
          onSetFilters({ minDurationMs: normalizeNumberFilter(value) }),
        placeholder: "0",
        type: "number",
        value: filters.minDurationMs?.toString() ?? "",
      },
      {
        key: "maxDurationMs",
        label: "Max duration (ms)",
        onChange: (value) =>
          onSetFilters({ maxDurationMs: normalizeNumberFilter(value) }),
        placeholder: "120000",
        type: "number",
        value: filters.maxDurationMs?.toString() ?? "",
      },
    ],
    [canonicalKinds, filters, handleKindsChange, kindsDraft, onSetFilters],
  );

  return (
    <FiltersToolbar
      isLoading={isLoading}
      activeFilterCount={activeFilterCount}
      onRefresh={onRefresh}
      onReset={handleReset}
      fromTimestampMs={filters.fromTimestampMs}
      toTimestampMs={filters.toTimestampMs}
      onSetDateRange={onSetDateRange}
      fields={fields}
    />
  );
};
